import {
  adminPageSeoListSchema,
  adminPageSeoSchema,
  deleteResultSchema,
  type AdminPageSeo,
  type CreatePageSeo,
  type DeleteResult,
  type UpdatePageSeo,
} from '@apuestapr/contracts';

import type { ApiClient, ApiResult } from '../api/client.ts';
import {
  getDashboardContentDefinition,
  type ContentCreateByType,
  type ContentItemByType,
  type ContentPreviewByType,
  type ContentStatusByType,
  type ContentUpdateByType,
} from './catalog.ts';
import type { ContentType } from '@apuestapr/contracts';

const contentPath = (type: ContentType): string => `/api/admin/content/${type}`;
const itemPath = (type: ContentType, id: number): string => `${contentPath(type)}/${String(id)}`;

export interface ContentApi {
  readonly list: <Type extends ContentType>(
    type: Type,
  ) => Promise<ApiResult<readonly ContentItemByType[Type][]>>;
  readonly detail: <Type extends ContentType>(
    type: Type,
    id: number,
  ) => Promise<ApiResult<ContentItemByType[Type]>>;
  readonly create: <Type extends ContentType>(
    type: Type,
    input: ContentCreateByType[Type],
    csrfToken: string,
  ) => Promise<ApiResult<ContentItemByType[Type]>>;
  readonly update: <Type extends ContentType>(
    type: Type,
    id: number,
    input: ContentUpdateByType[Type],
    csrfToken: string,
  ) => Promise<ApiResult<ContentItemByType[Type]>>;
  readonly changeStatus: <Type extends ContentType>(
    type: Type,
    id: number,
    input: ContentStatusByType[Type],
    csrfToken: string,
  ) => Promise<ApiResult<ContentItemByType[Type]>>;
  readonly preview: <Type extends ContentType>(
    type: Type,
    id: number,
  ) => Promise<ApiResult<ContentPreviewByType[Type]>>;
  readonly remove: (
    type: ContentType,
    id: number,
    csrfToken: string,
  ) => Promise<ApiResult<DeleteResult>>;
  readonly listPageSeo: () => Promise<ApiResult<readonly AdminPageSeo[]>>;
  readonly pageSeoDetail: (id: number) => Promise<ApiResult<AdminPageSeo>>;
  readonly createPageSeo: (
    input: CreatePageSeo,
    csrfToken: string,
  ) => Promise<ApiResult<AdminPageSeo>>;
  readonly updatePageSeo: (
    id: number,
    input: UpdatePageSeo,
    csrfToken: string,
  ) => Promise<ApiResult<AdminPageSeo>>;
  readonly removePageSeo: (id: number, csrfToken: string) => Promise<ApiResult<DeleteResult>>;
}

/** Typed endpoint methods only; request state stays in hooks. Arrays are returned without sorting. */
export const createContentApi = (client: ApiClient): ContentApi => ({
  list: <Type extends ContentType>(type: Type) => {
    const definition = getDashboardContentDefinition(type);
    return client.get(contentPath(type), definition.listSchema);
  },
  detail: <Type extends ContentType>(type: Type, id: number) => {
    const definition = getDashboardContentDefinition(type);
    return client.get(itemPath(type, id), definition.itemSchema);
  },
  create: <Type extends ContentType>(
    type: Type,
    input: ContentCreateByType[Type],
    csrfToken: string,
  ) => {
    const definition = getDashboardContentDefinition(type);
    return client.send('POST', contentPath(type), definition.itemSchema, {
      body: input,
      csrfToken,
    });
  },
  update: <Type extends ContentType>(
    type: Type,
    id: number,
    input: ContentUpdateByType[Type],
    csrfToken: string,
  ) => {
    const definition = getDashboardContentDefinition(type);
    return client.send('PUT', itemPath(type, id), definition.itemSchema, {
      body: input,
      csrfToken,
    });
  },
  changeStatus: <Type extends ContentType>(
    type: Type,
    id: number,
    input: ContentStatusByType[Type],
    csrfToken: string,
  ) => {
    const definition = getDashboardContentDefinition(type);
    return client.send('PATCH', `${itemPath(type, id)}/status`, definition.itemSchema, {
      body: input,
      csrfToken,
    });
  },
  preview: <Type extends ContentType>(type: Type, id: number) => {
    const definition = getDashboardContentDefinition(type);
    return client.get(`${itemPath(type, id)}/preview`, definition.previewSchema);
  },
  remove: (type, id, csrfToken) =>
    client.send('DELETE', itemPath(type, id), deleteResultSchema, { csrfToken }),
  listPageSeo: () => client.get('/api/admin/page-seo', adminPageSeoListSchema),
  pageSeoDetail: (id) => client.get(`/api/admin/page-seo/${String(id)}`, adminPageSeoSchema),
  createPageSeo: (input, csrfToken) =>
    client.send('POST', '/api/admin/page-seo', adminPageSeoSchema, {
      body: input,
      csrfToken,
    }),
  updatePageSeo: (id, input, csrfToken) =>
    client.send('PUT', `/api/admin/page-seo/${String(id)}`, adminPageSeoSchema, {
      body: input,
      csrfToken,
    }),
  removePageSeo: (id, csrfToken) =>
    client.send('DELETE', `/api/admin/page-seo/${String(id)}`, deleteResultSchema, { csrfToken }),
});
